import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
    FaHeart, FaBalanceScale, FaClipboardList, FaHandshake, FaRocket,
    FaUsers, FaGlobe, FaFileAlt, FaMicroscope, FaGraduationCap,
    FaLeaf, FaRobot, FaTheaterMasks, FaBook, FaSeedling,
    FaBuilding, FaUserShield, FaHandHoldingHeart, FaUniversity,
} from 'react-icons/fa';
import { FaTrophy, FaScaleUnbalanced } from 'react-icons/fa6';

import '../styles/OurStory.css';

const OurStory = () => {
    // Neutralized Values: Focus on "Human Rights" and "Professionalism" to cover LGBTQ+ and Gender Equality without being "partisan"
    const valuesAndCommitments = [
        { icon: <FaUserShield aria-hidden="true" />, title: 'Operational Excellence', description: 'Institutional-grade support for projects of all sizes.' },
        { icon: <FaBalanceScale aria-hidden="true" />, title: 'Mission Neutrality', description: 'We empower causes based on merit and impact, not personal ideology.' },
        { icon: <FaClipboardList aria-hidden="true" />, title: 'Total Transparency', description: 'Real-time financial visibility and radical accountability.' },
        { icon: <FaHandshake aria-hidden="true" />, title: 'Professional Partnership', description: 'Proven nonprofit practice meets modern, agile execution.' },
        { icon: <FaMicroscope aria-hidden="true" />, title: 'Evidence-Based Progress', description: 'Measurable outcomes over rhetoric in pursuit of human advancement.' },
        { icon: <FaHandHoldingHeart aria-hidden="true" />, title: 'Human Rights & Dignity', description: 'Universal equality and dignity for all individuals are core to our framework.' },
    ];

    // Expanded Causes: Includes all your requested sub-categories
    const causes = [
        { 
            icon: <FaBalanceScale aria-hidden="true" />, 
            title: 'Civil Liberties & Justice', 
            examples: ['Social Justice', 'Civil Rights & Advocacy', 'Immigration Reform', 'Economic Equality', 'Criminal Justice Reform'] 
        },
        { 
            icon: <FaGraduationCap aria-hidden="true" />, 
            title: 'Education & Literacy', 
            examples: ['STEM Programs', 'Arts Education', 'Adult Learning', 'Educational Equity', 'Early Learning'] 
        },
        { 
            icon: <FaHandHoldingHeart aria-hidden="true" />, 
            title: 'Health & Wellness', 
            examples: ['Mental Health', 'Medical Research', 'Public Health', 'Healthcare Access', 'Wellness Programs'] 
        },
        { 
            icon: <FaLeaf aria-hidden="true" />, 
            title: 'Environment & Energy', 
            examples: ['Climate Action', 'Conservation', 'Sustainable Energy', 'Environmental Justice', 'Resource Stewardship'] 
        },
        { 
            icon: <FaUniversity aria-hidden="true" />, 
            title: 'Arts, Culture & Media', 
            examples: ['Documentary Film', 'Performing Arts', 'Cultural Preservation', 'Community Arts', 'Public Media'] 
        },
        { 
            icon: <FaUsers aria-hidden="true" />, 
            title: 'Community Development', 
            examples: ['Affordable Housing', 'Food Security', 'Youth Programs', 'Infrastructure', 'Economic Opportunity'] 
        }
    ];

    const timeline = [
        {
            year: "2024",
            icon: <FaSeedling />,
            title: "Foundation",
            description: "Cause & Impact, Inc. established to bridge the gap between passionate changemakers and compliant operations.",
            impact: "Built on lived experience with fiscal sponsorship bottlenecks."
        },
        {
            year: "2024",
            icon: <FaBuilding />,
            title: "Architecture Formed",
            description: "Designed the core operational framework to eliminate silos and ensure audit-ready transparency from day one.",
            impact: "Launched financial dashboards for real-time project visibility."
        },
        {
            year: "2024",
            icon: <FaUsers />,
            title: "Core Team Formed",
            description: "The leadership team united finance, operations, and technology to build a mission-neutral support model.",
            impact: "Built the model we wished existed—fair, transparent, and modern."
        },
        {
            year: "2025",
            icon: <FaFileAlt />,
            title: "IRS Determination",
            description: "Received official IRS 501(c)(3) determination letter, confirming our regulatory status as a public charity.",
            impact: "Unlocked full tax-deductible fundraising capability for sponsored projects."
        },
        {
            year: "2025",
            icon: <FaRobot />,
            title: "Innovation in Compliance",
            description: "Integrated AI-driven compliance engines to deliver real-time guidance and automated reporting.",
            impact: "Redefined speed and accuracy in fiscal sponsorship management."
        },
        {
            year: "Future",
            icon: <FaGlobe />,
            title: "Scaling Impact",
            description: "Grow responsibly while deepening transparency, speed, and support for community-led projects.",
            impact: "Goal: Empower 1,000+ causes to raise $100M+ for healthier, more resilient communities."
        }
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                    }
                });
            },
            { threshold: 0.1 }
        );

        const elements = document.querySelectorAll('.os-reveal, .milestone');
        elements.forEach(el => observer.observe(el));

        return () => elements.forEach(el => observer.unobserve(el));
    }, []);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Cause & Impact, Inc.",
        "url": "https://www.causeandimpact.org",
        "founder": { "@type": "Person", "name": "Leighton Jacobus" },
        "foundingDate": "2024",
        "missionStatement": "Provide fiscal sponsorship and the related administrative, compliance, and governance support charitable programs need to operate effectively for public benefit."
    };

    return (
        <>
            <Helmet>
                <title>About | Cause & Impact — Mission-Neutral Fiscal Sponsorship</title>
                <meta
                    name="description"
                    content="We are a mission-neutral fiscal sponsor built by operators. We provide the infrastructure for diverse charitable causes to thrive through transparency and compliance."
                />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <main className="os-page">
                <header id="story" className="os-hero" aria-labelledby="story-title">
                    <div className="os-container os-narrow">
                        <h1 id="story-title" className="os-main-title">Our Story</h1>
                        <p className="os-lede">
                            At Cause & Impact, we believe administrative hurdles shouldn't stop great ideas. We exist to be the operational backbone—handling compliance, accounting, and logistics—so mission-driven leaders can focus on making a difference.
                        </p>
                    </div>
                </header>

                <section id="founder" className="os-section os-reveal" aria-labelledby="founder-title">
                    <div className="os-container os-narrow">
                        <div className="founder-grid">
                            <img
                                src="/assets/staffimages/FounderQuoteLeighton.png"
                                alt="Leighton Jacobus, Founder"
                                className="founder-photo"
                            />
                            <figure className="os-quote-standalone">
                                <blockquote>
                                    “I started Cause & Impact because I saw brilliant ideas choked out by outdated tech and quiet politics. <span className="os-quote-highlight">It is our duty to support good work based on its impact, not its ideology.</span> We built a modern launchpad where practical operations remove capacity limits, ensuring every compliant project gets the unbiased backing it needs to thrive.”
                                </blockquote>
                                <figcaption>— Leighton Jacobus, Founder & Chief Architect</figcaption>
                            </figure>
                        </div>
                    </div>
                </section>

                <section id="mission" className="os-section os-reveal" aria-labelledby="mission-title">
                    <div className="os-container os-narrow">
                        <h2 id="mission-title" className="os-section-title">Our Mission</h2>
                        <div className="os-card os-mission-card">
                            <p className="os-mission-text">
                                To provide community-led projects with the professional structure, governance, and protection they need to succeed. We manage the complexity so you can lead the change.
                            </p>
                        </div>
                        <nav className="os-doc-links" aria-label="Governance documents">
                            <a href="/docs/letter-of-determination" className="os-doc-button irs-letter" target="_blank" rel="noopener noreferrer">
                                <FaBook aria-hidden="true" /> IRS Determination
                            </a>
                            <a href="https://www.guidestar.org/" className="os-doc-button candid-profile" target="_blank" rel="noopener noreferrer">
                                <FaTrophy aria-hidden="true" /> Candid Profile
                            </a>
                        </nav>
                    </div>
                </section>

                <section id="values" className="os-section os-section-alt os-reveal" aria-labelledby="values-title">
                    <div className="os-container">
                        <h2 id="values-title" className="os-section-title">Our Values & Commitments</h2>
                        <div className="os-grid os-grid-3">
                            {valuesAndCommitments.map((v, i) => (
                                <article className="os-card" key={i}>
                                    <div className="os-card-icon">{v.icon}</div>
                                    <h3 className="os-card-title">{v.title}</h3>
                                    <p className="os-card-text">{v.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="causes" className="os-section os-reveal" aria-labelledby="causes-title">
                    <div className="os-container">
                        <h2 id="causes-title" className="os-section-title">The Scope of Our Support</h2>
                        <p className="os-section-intro">We provide the infrastructure for a wide range of charitable sectors.</p>
                        <div className="os-grid os-grid-3">
                            {causes.map((area, index) => (
                                <article className="cause-badge" key={index}>
                                    <div className="cause-badge-icon">{area.icon}</div>
                                    <h3 className="cause-badge-title">{area.title}</h3>
                                    {/* Sub-list added here for detail, ensure CSS handles wrapping */}
                                    <ul className="cause-badge-list">
                                        {area.examples.map((ex, idx) => (
                                            <li key={idx}>{ex}</li>
                                        ))}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="cta" className="os-section os-reveal" aria-labelledby="cta-title">
                    <div className="os-container os-narrow">
                        <h2 id="cta-title" className="os-section-title">Turn Passion into Impact</h2>
                        <p className="os-section-intro">
                            Whether you are building a local park or researching a cure, we are here to support your mission with world-class operations.
                        </p>
                        <div className="os-cta-actions">
                            <Link to="/apply" className="os-btn os-btn-primary">
                                <FaRocket aria-hidden="true" /> Apply for Sponsorship
                            </Link>
                            <Link to="/team" className="os-btn os-btn-secondary">
                                <FaUsers aria-hidden="true" /> Meet Our Team
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
};

export default OurStory;